import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import SetPricePanel from '../components/SetPricePanel';
import PageTitle from '../components/PageTitle';

const API_BASE = process.env.REACT_APP_API_URL || '';

const DSC_STATUSES = ['APPLIED','DOCUMENTS_VERIFIED','SUBMITTED_TO_PROVIDER','VIDEO_VERIFICATION_PENDING','ISSUED','REJECTED'];

const STATUS_STYLE = {
  APPLIED:                   { label: 'Applied',                  icon: '📨', cls: 'badge-submitted' },
  DOCUMENTS_VERIFIED:        { label: 'Docs Verified',            icon: '✅', cls: 'badge-approved' },
  SUBMITTED_TO_PROVIDER:     { label: 'With Provider',            icon: '🔄', cls: 'badge-under_review' },
  VIDEO_VERIFICATION_PENDING:{ label: 'Video Verification',       icon: '🎥', cls: 'badge-draft_sent' },
  ISSUED:                    { label: 'Issued',                   icon: '🔐', cls: 'badge-acknowledged' },
  REJECTED:                  { label: 'Rejected',                 icon: '❌', cls: 'badge-rejected' },
};

function DscBadge({ status }) {
  const s = STATUS_STYLE[status] || { label: status, icon: '•', cls: '' };
  return <span className={`badge ${s.cls}`}>{s.icon} {s.label}</span>;
}

// ── Admin DSC List ──────────────────────────────────────────────
export function AdminDscList() {
  const [apps, setApps]           = useState([]);
  const [filtered, setFiltered]   = useState([]);
  const [statusFilter, setFilter] = useState('ALL');
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    api.get('/api/dsc/all').then(r => { setApps(r.data); setFiltered(r.data); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let list = apps;
    if (statusFilter !== 'ALL') list = list.filter(a => a.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        a.applicantName?.toLowerCase().includes(q) ||
        a.pan?.toLowerCase().includes(q) ||
        a.dscType?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [statusFilter, search, apps]);

  const pendingCount = apps.filter(a => a.status === 'APPLIED').length;

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;

  return (
    <>
      <PageTitle title="DSC Applications" />
      <div className="page-hero">
        <div className="page-hero-label">Admin Panel</div>
        <h1>DSC Applications</h1>
        <p>{apps.length} total · {pendingCount} new</p>
      </div>
      <div className="page-wide">
        <div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
          {[
            { label: 'Total',         value: apps.length,                                         icon: '🔐' },
            { label: 'New',           value: apps.filter(a => a.status === 'APPLIED').length,     icon: '📨', highlight: true },
            { label: 'With Provider', value: apps.filter(a => a.status === 'SUBMITTED_TO_PROVIDER').length, icon: '🔄' },
            { label: 'Issued',        value: apps.filter(a => a.status === 'ISSUED').length,      icon: '✅' },
          ].map(s => (
            <div key={s.label} className="stat-card"
              style={s.highlight && s.value > 0 ? { border: '2px solid var(--gold)' } : {}}>
              <div style={{ fontSize: '1.3rem', marginBottom: '.2rem' }}>{s.icon}</div>
              <div className="stat-num">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Search name, PAN, email, DSC type..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220, padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: '#fff', outline: 'none' }} />
          <select value={statusFilter} onChange={e => setFilter(e.target.value)}
            style={{ padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: '#fff' }}>
            <option value="ALL">All Statuses</option>
            {DSC_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
          <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>{filtered.length} results</span>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr><th>#</th><th>Applicant</th><th>PAN</th><th>Type</th><th>Validity</th><th>Token</th><th>Tracking ID</th><th>Status</th><th>Date</th><th>Action</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>No applications found</td></tr>
                )}
                {filtered.map(a => (
                  <tr key={a.id}>
                    <td style={{ color: 'var(--muted)' }}>#{a.id}</td>
                    <td>
                      <strong style={{ fontSize: '.88rem' }}>{a.applicantName}</strong>
                      {a.organisationName && <div style={{ fontSize: '.72rem', color: 'var(--muted)' }}>{a.organisationName}</div>}
                      <div style={{ fontSize: '.75rem', color: 'var(--muted)' }}>{a.email}</div>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '.82rem' }}>{a.pan}</td>
                    <td style={{ fontSize: '.8rem' }}>
                      <span style={{ background: 'var(--bluel)', color: 'var(--blue)', fontSize: '.7rem', fontWeight: 700, padding: '.15rem .5rem', borderRadius: 4 }}>{a.dscType}</span>
                    </td>
                    <td style={{ fontSize: '.83rem' }}>{a.validity}</td>
                    <td style={{ fontSize: '.8rem', color: 'var(--muted)' }}>{a.tokenType}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '.78rem', color: 'var(--green)' }}>{a.trackingId || '—'}</td>
                    <td><DscBadge status={a.status} /></td>
                    <td style={{ fontSize: '.78rem', color: 'var(--muted)' }}>{new Date(a.appliedAt).toLocaleDateString('en-IN')}</td>
                    <td><Link to={`/admin/dsc/${a.id}`}><button className="btn btn-primary btn-sm">Review</button></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Admin DSC Detail ────────────────────────────────────────────
export function AdminDscDetail() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [app, setApp]           = useState(null);
  const [status, setStatus]     = useState('');
  const [note, setNote]         = useState('');
  const [trackingId, setTracking] = useState('');
  const [serialNo, setSerial]   = useState('');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);
  const [payment, setPayment]   = useState(null);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  useEffect(() => {
    Promise.all([
      api.get(`/api/dsc/${id}`),
      api.get(`/api/payment/for/DSC/${id}`).catch(() => ({ data: null })),
    ]).then(([d, p]) => {
      setApp(d.data);
      setStatus(d.data.status);
      setNote(d.data.adminNote || '');
      setTracking(d.data.trackingId || '');
      setSerial(d.data.serialNumber || '');
      setPayment(p.data || null);
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const saveStatus = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({ status });
      if (note)      params.append('note', note);
      if (trackingId) params.append('trackingId', trackingId);
      if (serialNo)  params.append('serialNumber', serialNo);
      const { data } = await api.patch(`/api/dsc/${id}/status?${params}`);
      setApp(data);
      showMsg('success', `Status updated to ${status.replace(/_/g, ' ')}`);
    } catch { showMsg('error', 'Update failed'); }
    finally { setSaving(false); }
  };

  const uploadDetails = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data } = await api.post(`/api/dsc/${id}/upload-details`, fd);
    setApp(data); setStatus(data.status);
    showMsg('success', 'DSC details uploaded — status set to Issued!');
  };

  const downloadFile = async (filename, label) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/files/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = label + '_' + (filename.includes('_') ? filename.substring(filename.indexOf('_') + 1) : filename);
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;
  if (!app)    return <div className="centered"><p>Application not found.</p></div>;

  return (
    <>
      <PageTitle title={`DSC Application #${app.id}`} />
      <div className="page-hero">
        <div className="page-hero-label">Admin — DSC Review</div>
        <h1>DSC Application #{app.id}</h1>
        <p>{app.applicantName} · {app.pan} · {app.dscType}</p>
      </div>
      <div className="page-wide">
        <button className="btn btn-outline btn-sm" style={{ marginBottom: '1.5rem' }} onClick={() => navigate('/admin/dsc')}>← Back</button>

        {msg && <div className={`alert alert-${msg.type}`} style={{ marginBottom: '1.5rem' }}>{msg.text}</div>}

        <div className="detail-grid">

          {/* Left */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* DSC Config */}
            <div className="card">
              <SectionTitle>DSC Configuration</SectionTitle>
              <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                {[app.dscType, app.validity, app.tokenType].map(v => v && (
                  <span key={v} style={{ background: 'var(--navy)', color: '#fff', fontSize: '.82rem', fontWeight: 600, padding: '.35rem .9rem', borderRadius: 20 }}>{v}</span>
                ))}
              </div>
              {app.trackingId && <InfoRow label="Tracking ID" value={app.trackingId} />}
              {app.serialNumber && <InfoRow label="Serial Number" value={app.serialNumber} />}
            </div>

            {/* Applicant */}
            <div className="card">
              <SectionTitle>Applicant Details</SectionTitle>
              <div className="two-col" style={{ gap: '.5rem' }}>
                <InfoRow label="Full Name"    value={app.applicantName} />
                <InfoRow label="PAN"          value={app.pan} />
                <InfoRow label="Date of Birth" value={app.dob} />
                <InfoRow label="Email"        value={app.email} />
                <InfoRow label="Phone"        value={app.phone} />
                {app.designation     && <InfoRow label="Designation"   value={app.designation} />}
                {app.organisationName && <InfoRow label="Organisation"  value={app.organisationName} />}
                {app.organisationType && <InfoRow label="Org Type"      value={app.organisationType} />}
                {app.cin             && <InfoRow label="CIN"            value={app.cin} />}
              </div>
            </div>

            {/* Address */}
            <div className="card">
              <SectionTitle>Address</SectionTitle>
              <div className="two-col" style={{ gap: '.5rem' }}>
                <InfoRow label="Address" value={app.address} />
                <InfoRow label="City"    value={app.city} />
                <InfoRow label="State"   value={app.state} />
                <InfoRow label="Pincode" value={app.pincode} />
              </div>
              {app.remarks && <div className="alert alert-info" style={{ marginTop: '1rem', fontSize: '.82rem' }}>💬 {app.remarks}</div>}
            </div>

            {/* Documents */}
            <div className="card">
              <SectionTitle>Documents (Download)</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
                {[['PAN Card', app.docPan], ['Aadhar Card', app.docAadhar],
                  ['Photo', app.docPhoto], ['Org Proof', app.docOrgProof],
                  ['Auth Letter', app.docAuthLetter]
                ].map(([label, filename]) => filename ? (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.6rem .9rem', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                      <span>📄</span>
                      <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--green)' }}>{label}</span>
                    </div>
                    <button onClick={() => downloadFile(filename, label)}
                      style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 7, padding: '.32rem .8rem', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>
                      ⬇ Download
                    </button>
                  </div>
                ) : (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.6rem .9rem', background: 'var(--creamd)', borderRadius: 8, opacity: .5 }}>
                    <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>— {label}</span>
                    <span style={{ fontSize: '.72rem', color: 'var(--muted)' }}>Not uploaded</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Upload DSC Details */}
            <div className="card">
              <SectionTitle>Upload DSC Details / Certificate</SectionTitle>
              <p style={{ fontSize: '.82rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                After DSC is issued, upload the details file — user can download it from their dashboard. Status auto-sets to Issued.
              </p>
              {app.docDscDetails ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.75rem 1rem', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 10 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--green)' }}>✅ DSC details uploaded</div>
                    <div style={{ fontSize: '.72rem', color: 'var(--muted)' }}>User can download from their dashboard</div>
                  </div>
                  <button onClick={() => downloadFile(app.docDscDetails, 'DSC_Details')}
                    style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 7, padding: '.32rem .8rem', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>
                    ⬇ Download
                  </button>
                </div>
              ) : (
                <input type="file" accept=".pdf,.jpg,.png,.zip" style={{ fontSize: '.82rem' }}
                  onChange={e => { if (e.target.files[0]) uploadDetails(e.target.files[0]); }} />
              )}
            </div>
          </div>

          {/* Right: Status panel */}
          <div>
            <div className="card" style={{ position: 'sticky', top: '5rem' }}>
              <SectionTitle>Update Status</SectionTitle>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '.75rem', color: 'var(--muted)', marginBottom: '.35rem' }}>Current</div>
                <DscBadge status={app.status} />
              </div>

              <div className="field" style={{ marginBottom: '1rem' }}>
                <label>Set Status</label>
                <select value={status} onChange={e => setStatus(e.target.value)}>
                  {DSC_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>

              <div className="field" style={{ marginBottom: '1rem' }}>
                <label>Tracking ID</label>
                <input value={trackingId} onChange={e => setTracking(e.target.value)}
                  placeholder="Provider tracking number"
                  style={{ fontFamily: 'monospace', fontSize: '.85rem' }} />
                <span className="hint">From eMudhra / Sify / NSDL provider</span>
              </div>

              <div className="field" style={{ marginBottom: '1rem' }}>
                <label>DSC Serial Number</label>
                <input value={serialNo} onChange={e => setSerial(e.target.value)}
                  placeholder="Enter after issuance"
                  style={{ fontFamily: 'monospace', fontSize: '.85rem' }} />
              </div>

              <div className="field" style={{ marginBottom: '1.25rem' }}>
                <label>Note to User</label>
                <textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Shown on user's dashboard..." />
              </div>

              <button className="btn btn-gold" style={{ width: '100%', justifyContent: 'center' }}
                onClick={saveStatus} disabled={saving}>
                {saving ? <><span className="spinner" /> Saving...</> : '💾 Update Status'}
              </button>

              {/* Payment */}
              <SetPricePanel
                serviceType="DSC"
                serviceId={app.id}
                serviceDesc={`DSC Application #${app.id} — ${app.dscType} (${app.validity})`}
                userId={app.userId}
                payment={payment}
                onUpdated={p => setPayment(p)}
              />

              {/* Workflow */}
              <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid var(--creamd)' }}>
                <SectionTitle>Workflow</SectionTitle>
                {[
                  ['1', 'APPLIED',                    '⬇ Download & verify documents'],
                  ['2', 'DOCUMENTS_VERIFIED',          '✅ Docs OK, submit to provider'],
                  ['3', 'SUBMITTED_TO_PROVIDER',       '🔄 Enter tracking ID'],
                  ['4', 'VIDEO_VERIFICATION_PENDING',  '🎥 Schedule video call with user'],
                  ['5', 'ISSUED',                      '⬆ Upload DSC details + serial no.'],
                ].map(([n, s, desc]) => (
                  <div key={s} style={{ display: 'flex', gap: '.6rem', marginBottom: '.5rem' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: app.status === s ? 'var(--green)' : 'var(--creamd)', color: app.status === s ? '#fff' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{n}</div>
                    <div>
                      <div style={{ fontSize: '.75rem', fontWeight: 600, color: app.status === s ? 'var(--green)' : 'var(--text)' }}>{s.replace(/_/g, ' ')}</div>
                      <div style={{ fontSize: '.7rem', color: 'var(--muted)' }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--creamd)', fontSize: '.73rem', color: 'var(--muted)' }}>
                Applied: {new Date(app.appliedAt).toLocaleString('en-IN')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontWeight: 600, fontSize: '.82rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--navy)', marginBottom: '1rem' }}>{children}</div>;
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ padding: '.4rem 0', borderBottom: '1px solid var(--creamd)', fontSize: '.83rem' }}>
      <div style={{ color: 'var(--muted)', fontSize: '.72rem', marginBottom: '.1rem' }}>{label}</div>
      <div style={{ fontWeight: 500, color: 'var(--navy)' }}>{value}</div>
    </div>
  );
}